import axios from 'axios';
import { store } from '../../redux/store';
import {
  getUserFail,
  logoutUser,
  updateTokens,
} from '../../redux/slices/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';

const axiosAuthService = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/api`,
});

const axiosApiService = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/api`,
});

const ImageBaseURL = '';

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = callback => {
  refreshSubscribers.push(callback);
};

const onRefreshed = newAccessToken => {
  refreshSubscribers.forEach(callback => callback(newAccessToken));
  refreshSubscribers = [];
};

const refreshToken = async refreshToken => {
  try {
    const response = await axiosAuthService.post('/auth/refresh/', {
      refresh: refreshToken,
    });
    return response.data;
  } catch (error) {
    console.error(
      'Error refreshing token:',
      error.response?.data || error.message
    );
    throw error;
  }
};

const setupInterceptors = () => {
  const requestInterceptor = config => {
    const state = store.getState();
    const user = state.user.user;

    if (user?.access) {
      config.headers.Authorization = `Bearer ${user.access}`;
    }

    if (user?.user?.TL_name && !config.headers.TL) {
      config.headers.TL = user.user.TL_name;
    }

    return config;
  };

  //// New Code to update the token values with new implementation in userSlice
  const responseErrorInterceptor = async error => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (originalRequest.url.includes('refresh/')) {
        // If the refresh token request failed with 401, logout
        store.dispatch(logoutUser());
        return Promise.reject(error);
      }

      if (originalRequest.url.includes('refresh/')) {
        // If the refresh token request failed with 401, logout
        store.dispatch(logoutUser());
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        isRefreshing = true;
        const state = store.getState();
        const user = state.user.user;

        try {
          const newTokens = await refreshToken(user.refresh); // Dispatch updateTokens to update only access and refresh tokens
          store.dispatch(
            updateTokens({ access: newTokens.access, refresh: user.refresh })
          );
          onRefreshed(newTokens.access);
          isRefreshing = false;
        } catch (refreshError) {
          isRefreshing = false;
          store.dispatch(
            getUserFail(refreshError.response?.data || refreshError.message)
          );
          refreshSubscribers = [];
          return Promise.reject(refreshError);
        }
      }

      return new Promise(resolve => {
        subscribeTokenRefresh(newAccessToken => {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          resolve(axios(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  };

  axiosAuthService.interceptors.request.use(requestInterceptor, error =>
    Promise.reject(error)
  );
  axiosAuthService.interceptors.response.use(
    response => response,
    responseErrorInterceptor
  );

  axiosApiService.interceptors.request.use(requestInterceptor, error =>
    Promise.reject(error)
  );
  axiosApiService.interceptors.response.use(
    response => response,
    responseErrorInterceptor
  );
};

/**
 * Hook: useAxiosInterceptors
 * Initializes Axios interceptors based on the current user state.
 */
export const useAxiosInterceptors = setIsInterceptorReady => {
  const user = useSelector(state => state.user);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!user || !user.access) {
      setIsInterceptorReady(true); // Set the interceptor as ready if no user is present
      return;
    }

    const token = user.access;
    const refresh = user.refresh;
    const TL = user.user?.TL_name;

    if (token) {
      sessionStorage.setItem('authorization', token);
    }

    if (refresh) {
      sessionStorage.setItem('refresh_token', refresh);
    }

    if (TL) {
      sessionStorage.setItem('TL_name', TL);
    }

    setupInterceptors(dispatch);

    // Indicate that the interceptors are ready
    setIsInterceptorReady(true);
  }, [user, dispatch, setIsInterceptorReady]);
};

// Set up the interceptors
setupInterceptors();

// Correctly export ImageBaseURL along with the services
export default axiosAuthService;
export { axiosApiService, ImageBaseURL }; // Ensure ImageBaseURL is exported
